/**
 * Blackwaxx&Co add-to-cart helper. Adds a variant the way Horizon's own product form does, so the header cart count,
 * the cart drawer (and its auto-open) and every other cart listener stay in step:
 *   1. dispatch a CartLinesUpdateEvent carrying a promise,
 *   2. POST to /cart/add.js asking for the cart drawer's section HTML,
 *   3. re-read the cart and resolve the promise with it.
 *
 * Also wires every [data-bw-quick-add] button (product cards) to add one of its variant.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @param {object} options
 * @param {string|number} options.id - Variant id
 * @param {number} [options.quantity]
 * @param {Element} [options.source] - Element the event is dispatched from
 * @param {string} [options.productId]
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
export async function addToCart({ id, quantity = 1, source = document.body, productId }) {
  const formData = new FormData();
  formData.append('id', String(id));
  formData.append('quantity', String(quantity));

  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }
  formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity }],
      promise: deferred.promise,
    })
  );

  const refresh = () => fetch(`${Theme.routes.cart_url}.json`, { credentials: 'same-origin' }).then((r) => r.json());

  try {
    const config = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    }).then((r) => r.json());

    if (response.status) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      const cart = await refresh();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'bw-cart', itemCount: quantity, productId },
      });
      return { ok: false, message: response.description || response.message };
    }

    const cart = await refresh();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bw-cart',
        itemCount: quantity,
        productId,
        sections: response.sections,
        didError: false,
      },
    });
    return { ok: true };
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
    return { ok: false, message: error?.message };
  }
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-bw-quick-add]') : null;
  if (!button || button.hasAttribute('disabled')) return;
  event.preventDefault();
  button.setAttribute('aria-busy', 'true');
  button.setAttribute('disabled', '');
  await addToCart({
    id: button.dataset.variantId,
    productId: button.dataset.productId,
    source: button,
  });
  button.removeAttribute('disabled');
  button.removeAttribute('aria-busy');
});
